import * as React from "react";
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { Viewer } from "./js/main";
import { sendCustomEvent } from "../events";
import { useEffect, useState } from "react";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import {
  TagCreateDialog,
  TagDeleteDialog,
  TagEditDialog
} from "../folder";
import { isStaticSite } from "../electron-modules";
import { DialogContent } from "@material-ui/core";
import { DraggableDialog } from "../dialogs";
import EnhancedTable from "../enhanced-table";

export interface LabelContextData {
  labelID: number
  pointID: number
  coordinate: object
}

export interface TagContextData {
  tagIDs: string[]
  coordinate: object
}

export interface ContextData {
  labelData?: LabelContextData
  tagData?: TagContextData
  modelData?: object[]
}

interface ContextMenuItem {
  text: string
  disabled: boolean
  visible?: boolean
  callback: Function
}

export const initialContextState = {
  mouseX: null,
  mouseY: null,
  isAerial: false,
  labelData: null as LabelContextData,
  tagData: null as TagContextData,
  modelData: null as object
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    menuItem: {
      fontSize: "0.875em",
    }
  }),
);

export const ContextMenu = (props) => {
  const classes = useStyles();
  const viewer = props.viewer as Viewer;

  const [addTagOpen, setAddTagOpen] = useState(false);
  const [modelProperties, setModelProperties] = useState(null);
  const [deleteTagOpen, setDeleteTagOpen] = useState(false);
  const [tagsToDelete, setTagsToDelete] = useState([]);
  const [tagToEdit, setTagToEdit] = useState(null);
  const [contextState, setContextState] = useState({...initialContextState});
  const {isAerial, labelData, tagData, modelData} = contextState;

  const onClose = () => {
    setContextState({
      ...contextState,
      mouseX: null,
      mouseY: null
    });

    viewer?.clearCadHighlight();
  };

  const onEnter = () => {
    props.clearTooltipText();
  };

  const getMenuItems = () => {
    let numLabelsSelected = viewer.numLabelsHighlighted();

    const labelSelected = labelData ? labelData.labelID !== null : false;
    const labelPointSelected = labelData ? labelData.pointID !== null : false;
    const labelCoordSelected = labelData ? labelData.coordinate !== null : false;
    const canAddNewTag = tagData ? tagData.coordinate !== null : false;
    const numTagsSelected = tagData ? tagData.tagIDs.length  : 0;
    const hasTagData = !!tagData;
    const hasModelData = !!modelData;

    let items = [];

    if (numLabelsSelected > 0) {
      // Labels are selected
      items = [
        {
          text: numLabelsSelected === 1
            ? `Delete Selected Label`
            : `Delete Selected Labels (${numLabelsSelected})`,
          disabled: viewer.anyLabelSelected(),
          callback: () => {
            viewer.deleteHighlighted();
          }
        },
        {
          text: `Delete Selected Point`,
          disabled: !labelPointSelected,
          callback: () => {
            viewer.deleteLabelPoint(labelData.pointID);
          }
        },
        {
          text: numLabelsSelected === 1
            ? `Unselect Selected Label`
            : `Unselect Selected Labels (${numLabelsSelected})`,
          disabled: viewer.anyLabelSelected(),
          callback: () => {
            viewer.unHighlightAll();
          }
        },
        {
          text: "Insert Point",
          disabled: !labelCoordSelected || labelPointSelected,
          callback: () => {
            viewer.insertLabelPoint(labelData.labelID, labelData.coordinate);
          }
        },
        {
          text: "Edit From Here",
          disabled: !labelCoordSelected || labelPointSelected,
          callback: () => {
            viewer.editLabel(labelData.labelID, labelData.coordinate);
          }
        },
        {
          text: "Find in List",
          disabled: !labelSelected,
          callback: () => {
            viewer.moveToLabel(labelData.labelID);
          }
        },
      ];
    } else {
      // Standard context menu
      items = [
        {
          text: "Add New Tag",
          disabled: !canAddNewTag || numTagsSelected > 0,
          visible: !isStaticSite && hasTagData,
          callback: () => {
            setAddTagOpen(true);
          }
        },
        {
          text: numTagsSelected <= 1
            ? `Delete Selected Tag`
            : `Delete Selected Tags (${numTagsSelected})`,
          visible: !isStaticSite && (numTagsSelected > 0),
          callback: () => {
            setDeleteTagOpen(true);
            setTagsToDelete(tagData.tagIDs);
          }
        },
        {
          text: "Edit Selected Tag",
          visible: !isStaticSite && (numTagsSelected === 1),
          callback: () => {
            setTagToEdit(tagData.tagIDs[0]);
          }
        },
        {
          text: `Model Properties`,
          visible: hasModelData,
          callback: () => {
            setModelProperties(modelData);
          }
        },
        {
          text: `${viewer.orbitState ? "Disable" : "Enable"} 3D Mode`,
          disabled: !(viewer.hasPoints && viewer.cameraSelected),
          visible: !isAerial,
          callback: () => {
            viewer.toggleOrbitState();
          }
        },
        {
          text: "Reset 3D Mode",
          disabled: !viewer.orbitState,
          visible: !isAerial,
          callback: () => {
            viewer.resetCamera();
          }
        }
      ];
    }

    return items as ContextMenuItem[];
  };

  /** Context menu event listener */
  useEffect(() => {
    const callback = (contextEvent) => {
      const {event, isAerial, contextData} = contextEvent.detail;

      setContextState({
        mouseX: event.clientX - 2,
        mouseY: event.clientY - 4,
        isAerial,
        ...contextData
      });
    };

    document.addEventListener("context-menu", callback);

    return () => {
      document.removeEventListener("context-menu", callback);
    };
  }, []);

  let options = viewer ? getMenuItems() : [];
  options = options.filter(item => {
    return item.hasOwnProperty("visible")
      ? item.visible
      : true;
  });

  const contextOpen = (contextState.mouseY !== null) && options.length > 0;

  const anchorPosition = contextOpen
    ? {top: contextState.mouseY, left: contextState.mouseX}
    : undefined;

  return (
    <React.Fragment>
      <Menu
        keepMounted
        open={contextOpen}
        onClose={onClose}
        onEnter={onEnter}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition}
      >
        {options.map(option => {
          return (
            <MenuItem
              key={option.text}
              disabled={option.disabled}
              className={classes.menuItem}
              onClick={() => {
                onClose();
                option.callback();
              }}
            >
              {option.text}
            </MenuItem>
          );
        })}
      </Menu>

      {/* New Tag Dialog */}
      <TagCreateDialog
        open={addTagOpen}
        setOpen={setAddTagOpen}
        coordinate={tagData?.coordinate}
      />

      {/* Edit Tag Dialog */}
      <TagEditDialog
        tagToEdit={tagToEdit}
        setTagToEdit={setTagToEdit}
      />

      {/* Delete Tag Dialog */}
      <TagDeleteDialog
        open={deleteTagOpen}
        setOpen={setDeleteTagOpen}
        toDelete={tagsToDelete}
      />

      <ModelProperties
        data={modelProperties}
        setData={setModelProperties}
      />

    </React.Fragment>
  );
};

const ModelProperties = (props) => {
  const {data, setData} = props;

  const open = data ? data.length > 0 : false;

  const columns = [
    {id: 'property', label: 'Property'},
    {id: 'value', label: 'Value'}
  ];

  const rows = [];
  data?.forEach((property, index) => {
    rows.push({
      id: index,
      ...property
    });
  });

  return (
    <DraggableDialog
      narrow
      open={open}
      title="Model Properties"
      onClose={() => {
        setData(null);
      }}
    >
      <DialogContent>
        <EnhancedTable
          dense
          wordBreak
          pageSize={25}
          columns={columns}
          rows={rows}
        />
      </DialogContent>
    </DraggableDialog>
  );
};

export const showContextMenu = (event, isAerial, contextData = {} as ContextData) => {
  event.preventDefault();
  sendCustomEvent("context-menu", {event, isAerial, contextData});
};